import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import BootstrapTable from 'react-bootstrap-table-next';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Input } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { moveItem } from 'services/arrays/moveItem';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import translations from 'services/translations/translations';

import { useCurrency } from 'services/offers/currency';
import IFormState from './IFormState';
import getPriceSummary from './getPriceSummary';
import RowOrder from './calculationWindow/RowOrder';
import { getMarginClass } from './calculationWindow/getMarginRatioClassName';

import 'style/offers/MarginRatio.css';
import 'style/offers/OfferProductPriceSummary.css';

/**
 * The alternatives of the position. Their order can be changed here as the rows of the
 * calculation window: the alternatives are shown in this order in the calculation window
 * and the PDF, the topmost first.
 */
const VersionsSummaryTable = ({
  formState,
  locked,
  setFormState,
}: {
  formState: IFormState;
  locked?: boolean;
  setFormState: (formState: IFormState) => void;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common, translations.offers]);
  const currency = useCurrency();
  const formStateRef = useRef(formState);
  const [reordering, setReordering] = useState(false);
  const canReorder = !locked && formState.versions.length > 1;
  const isReordering = reordering && canReorder;

  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  if (formState.unit === '') {
    return null;
  }

  const moveVersion = (fromIndex: number, toIndex: number) =>
    setFormState({
      ...formStateRef.current,
      versions: moveItem(formStateRef.current.versions, fromIndex, toIndex),
    });

  const data = formState.versions.map((version, index) => {
    const priceSummary = getPriceSummary(
      version.installations,
      version.subproducts,
      numericStringToFloat(formState.quantity)
    );

    const getPrimaryVersionNotice = () =>
      version.isPrimary
        ? ` (${t(`${translations.calculationWindow}:primary`)})`
        : '';

    return {
      contributionMargin: formatGroupedNumber(
        priceSummary.shownContributionMargin
      ),
      contributionMarginForUnit: formatGroupedNumber(
        priceSummary.shownContributionMarginForUnit
      ),
      contributionMarginRatio: formatGroupedNumber(
        priceSummary.contributionMarginRatio
      ),
      expenseSum: formatGroupedNumber(priceSummary.expenseSum),
      expenseSumForUnit: formatGroupedNumber(priceSummary.expenseSumForUnit),
      id: version.id,
      isPrimary: version.isPrimary,
      // Below 20 % red, above 30 % green, as in the calculation window
      marginClass:
        priceSummary.sellingPrice === 0
          ? ''
          : getMarginClass(priceSummary.contributionMarginRatio),
      orderIndex: index,
      pdfIndex: index,
      showInOfferPdf: version.showInOfferPdf,
      sellingPrice: formatGroupedNumber(priceSummary.sellingPrice),
      sellingPriceForUnit: formatGroupedNumber(
        priceSummary.sellingPriceForUnit
      ),
      name: version.name + getPrimaryVersionNotice(),
    };
  });

  const unit = t(`${translations.common}:units:${formState.unit}`);
  const percentFormatter = (cell: any, row: { marginClass: string }) => (
    <span className={`margin-ratio ${row.marginClass}`}>{`${cell}%`}</span>
  );
  const priceFormatter = (cell: any) => `${cell}${currency}`;

  // As in the summary of the offer positions: numbers aligned by their last character,
  // the key figures in the colour of the theme and bold
  const numeric = {
    align: 'right' as const,
    headerAlign: 'right' as const,
    style: {
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap' as const,
    },
  };
  const keyFigure = 'theme-text-color key-figure';
  const keyNumeric = {
    ...numeric,
    classes: keyFigure,
    headerClasses: keyFigure,
  };
  const priceForUnitFormatter = (cell: any) => `${cell}${currency}/${unit}`;

  // The alternatives are told apart by their place: unsaved ones all have the id 0
  const onCheckboxClick =
    (versionIndex: number) => (e: ChangeEvent<HTMLInputElement>) => {
      setFormState({
        ...formStateRef.current,
        versions: formStateRef.current.versions.map((version, index) =>
          index === versionIndex
            ? {
                ...version,
                showInOfferPdf: e.target.checked,
              }
            : version
        ),
      });
    };

  const pdfFormatter = (versionIndex: number): JSX.Element => {
    const version = formState.versions[versionIndex];

    if (version === undefined) {
      return <></>;
    }

    return (
      <Input
        className="pdf-checkbox"
        checked={version.showInOfferPdf}
        onChange={onCheckboxClick(versionIndex)}
        type="checkbox"
      />
    );
  };

  return (
    <div className="version-summary">
      <hr />
      <div className="version-summary-header">
        <h3>{t(`${translations.calculationWindow}:Version summary`)}</h3>
        {canReorder ? (
          <div className="version-order-toolbar">
            {isReordering ? (
              <span className="version-order-info">
                {t(`${translations.calculationWindow}:Version order info`)}
              </span>
            ) : null}
            <PrimaryButton
              className={isReordering ? 'version-order-active' : undefined}
              onClick={() => setReordering(!reordering)}
              size="sm"
            >
              {isReordering
                ? t(`${translations.calculationWindow}:Version order done`)
                : t(`${translations.calculationWindow}:Change version order`)}
            </PrimaryButton>
          </div>
        ) : null}
      </div>
      <BootstrapTable
        bordered={false}
        columns={[
          ...(isReordering
            ? [
                {
                  dataField: 'orderIndex',
                  formatter: (index: number) => (
                    <RowOrder
                      count={formState.versions.length}
                      index={index}
                      move={moveVersion}
                      reordering
                    >
                      {null}
                    </RowOrder>
                  ),
                  headerStyle: () => ({ width: '4.5rem' }),
                  text: '#',
                },
              ]
            : []),
          {
            dataField: 'name',
            text: t(`${translations.common}:Name`),
          },
          // In the order of the summary of the offer positions: the selling price first,
          // the key figures highlighted as there
          {
            ...keyNumeric,
            dataField: 'sellingPriceForUnit',
            formatter: priceForUnitFormatter,
            text: `${t(
              `${translations.offers}:Selling price`
            )} ${currency}/${unit}`,
          },
          {
            ...numeric,
            dataField: 'expenseSumForUnit',
            formatter: priceForUnitFormatter,
            text: `${t(`${translations.offers}:Expense`)} ${currency}/${unit}`,
          },
          {
            ...keyNumeric,
            dataField: 'contributionMarginForUnit',
            formatter: priceForUnitFormatter,
            text: `${t(
              `${translations.offers}:Contribution margin`
            )} ${currency}/${unit}`,
          },
          {
            ...numeric,
            dataField: 'sellingPrice',
            formatter: priceFormatter,
            text: `${t(`${translations.offers}:Selling price`)} ${currency}`,
          },
          {
            ...numeric,
            dataField: 'expenseSum',
            formatter: priceFormatter,
            text: `${t(`${translations.offers}:Expense`)} ${currency}`,
          },
          {
            ...numeric,
            dataField: 'contributionMargin',
            formatter: priceFormatter,
            text: `${t(
              `${translations.offers}:Contribution margin`
            )} ${currency}`,
          },
          {
            ...keyNumeric,
            dataField: 'contributionMarginRatio',
            formatter: percentFormatter,
            headerStyle: () => ({ width: '6rem' }),
            text: `${t(`${translations.offers}:Contribution margin`)} %`,
          },
          {
            dataField: 'pdfIndex',
            formatter: pdfFormatter,
            headerStyle: () => ({ width: '3rem' }),
            text: 'PDF',
          },
        ]}
        data={data}
        keyField="orderIndex"
        // The primary alternative stands out when there are several
        rowClasses={(row: { isPrimary: boolean }) =>
          formState.versions.length > 1 && row.isPrimary
            ? 'primary-version-row'
            : ''
        }
      />
    </div>
  );
};

export default VersionsSummaryTable;
